import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import RatingService from 'services/rating';
import { setCookie } from 'helpers/cookies';
import DotLoader from 'components/Utlility/DotLoader';
import styles from './RatingModal.module.scss';

const RatingModal = ({ roomId, gamesPlayed, platform, onClose }) => {
    const { user, isLoggedIn } = useSelector(state => state.auth);

    const [show, setShow] = useState(false);
    const [rating, setRating] = useState(0);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [showFeedback, setShowFeedback] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [ratingId, setRatingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const uniqueId = localStorage.getItem('uniqueId');
    const userEmail = isLoggedIn ? user?.email : null;

    useEffect(() => {
        setTimeout(() => setShow(true), 10);
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    const handleStarClick = async (selectedRating) => {
        if (submitting) return;

        setRating(selectedRating);
        setSubmitting(true);

        try {
            const result = await RatingService.submitRating(
                uniqueId, userEmail, selectedRating, roomId, gamesPlayed, platform
            );

            if (result.ratingId) {
                setRatingId(result.ratingId);
            }

            if (selectedRating <= 3) {
                setShowFeedback(true);
            } else {
                // Show thank you briefly before closing
                showThankYouAndClose();
            }
        } catch (err) {
            console.error('Failed to submit rating:', err);
            handleClose();
        } finally {
            setSubmitting(false);
        }
    };

    const handleFeedbackSubmit = async () => {
        if (submitting) return;

        setSubmitting(true);

        if (ratingId && feedback.trim()) {
            try {
                await RatingService.updateRatingFeedback(ratingId, feedback);
            } catch (err) {
                console.error('Failed to update feedback:', err);
            }
        }

        setSubmitting(false);
        showThankYouAndClose();
    };

    const showThankYouAndClose = () => {
        setShowFeedback(false);
        setShowThankYou(true);
        //setTimeout(() => {
        //    handleClose();
        //}, 1500);
    };

    const handleClose = () => {
        setCookie('game-rating-shown', 'true', 7);
        setShow(false);
        setTimeout(() => onClose(), 500);
    };

    const handleSkip = () => {
        setCookie('game-rating-shown', 'true', 7);
        setShow(false);
        setTimeout(() => onClose(), 500);
    };

    const renderStars = () => {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            const isFilled = i <= (hoveredRating || rating);
            stars.push(
                <button
                    key={i}
                    type="button"
                    className={`${styles.star} ${isFilled ? styles.filled : ''}`}
                    onMouseEnter={() => setHoveredRating(i)}
                    onMouseLeave={() => setHoveredRating(0)}
                    onClick={() => handleStarClick(i)}
                    disabled={submitting || showFeedback}
                    aria-label={`Rate ${i} star${i > 1 ? 's' : ''}`}
                >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                </button>
            );
        }
        return stars;
    };

    return (
        <div className={`${styles.screenOverlay} ${show ? styles.show : ''}`}>
            <div className={styles.screenOverlayBG} onClick={handleSkip}></div>
            <div className={styles.modalContainer}>
                <div className={styles.modal}>
                    <button className={styles.closeButton} onClick={handleSkip}>
                        &times;
                    </button>

                    {showThankYou ? (
                        <div className={styles.content}>
                            <div className={styles.thankYouSection}>
                                <div className={styles.thankYouIcon}>&#10003;</div>
                                <h1>Thanks for your feedback!</h1>
                            </div>
                        </div>
                    ) : !showFeedback ? (
                        <div className={styles.content}>
                            <h1>How are you enjoying Game.City?</h1>
                            <div className={styles.starContainer}>
                                {submitting ? (
                                    <DotLoader size="medium" variant="light" />
                                ) : (
                                    renderStars()
                                )}
                            </div>
                            <p className={styles.subtitle}>Tap a star to rate</p>
                        </div>
                    ) : (
                        <div className={styles.content}>
                            <h1>What can we do better?</h1>
                            <p className={styles.ratingReceived}>Thanks for your rating!</p>
                            <textarea
                                className={styles.feedbackInput}
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                placeholder="Share your thoughts with us..."
                                rows={4}
                                maxLength={2000}
                            />
                            <div className={styles.buttonGroup}>
                                <button
                                    onClick={handleClose}
                                    className={`${styles.button} ${styles.small} ${styles.alternate}`}
                                    disabled={submitting}
                                >
                                    Skip
                                </button>
                                <button
                                    onClick={handleFeedbackSubmit}
                                    className={`${styles.button} ${styles.small}`}
                                    disabled={submitting}
                                >
                                    {submitting ? <DotLoader size="medium" variant="light" /> : 'Submit'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RatingModal;
